import NumerologyAlignedScorers from "@/components/NumerologyAlignedScorers";
import { PlayerName } from "@/components/PlayerCardDialog";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Loader2, Flame } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MATCH_STYLE = {
  "DIRECT MATCH": "bg-lime-500 text-slate-950",
  "REDUCED MATCH": "bg-cyan-950 text-cyan-300 border border-cyan-800",
  "REDUCTION GROUP MATCH": "bg-amber-950 text-amber-300 border border-amber-800",
  "NO MATCH": "bg-slate-800/60 text-slate-500",
};

const DailyHeader = ({ daily }) => (
  <div className="rounded-xl border border-fuchsia-900/40 bg-gradient-to-r from-fuchsia-950/20 to-slate-900/20 p-4" data-testid="numerology-daily">
    <h2 className="font-display text-lg font-bold uppercase tracking-wide text-fuchsia-300">Play'Em Str8 Daily</h2>
    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-slate-400">{daily.date_label}</p>
    <div className="mt-3 flex flex-wrap gap-2">
      {[["Day-Date", daily.day_number], ["Universal", daily.universal], ["Planet", daily.planet], ["Hidden", daily.hidden]].map(([l, v]) => (
        <div key={l} className="flex flex-col items-center rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-2">
          <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{l}</span>
          <span className="font-display text-lg font-bold text-fuchsia-300">{v}</span>
        </div>
      ))}
      <div className="flex flex-col rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-2">
        <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Core</span>
        <span className="font-display text-lg font-bold text-cyan-300">{daily.core.join(" • ")}</span>
      </div>
    </div>
    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[9px] text-slate-500">
      <span>DIRECT SETS: <span className="text-slate-400">{daily.direct_sets.join(", ")}</span></span>
      <span>EXTENDED: <span className="text-slate-400">{daily.extended_sequences.join(", ")}</span></span>
    </div>
  </div>
);

const PlayerRow = ({ p, team }) => {
  const hit = p.aligned_scored;
  return (
    <div
      className={`flex items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5 transition ${
        hit
          ? "border-lime-400/70 bg-lime-500/10 shadow-[0_0_0_1px_rgba(163,230,53,0.35)]"
          : "border-slate-800 bg-slate-900/40"
      }`}
      data-testid={`num-player-${p.name}`}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        {hit && <Flame className="h-3.5 w-3.5 shrink-0 text-lime-400" data-testid={`num-hit-${p.name}`} />}
        <span className="font-mono text-[12px] font-bold text-cyan-400">{p.jersey != null ? `#${p.jersey}` : "#—"}</span>
        <PlayerName name={p.name} team={team} className={`truncate font-display text-[12px] font-semibold uppercase tracking-tight ${hit ? "text-lime-200" : "text-slate-100"}`} />
        {p.position && <span className="rounded bg-slate-800 px-1 font-mono text-[9px] text-slate-400">{p.position}</span>}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {p.scored > 0 && (
          <span className="rounded bg-rose-600 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-white" data-testid={`num-goal-${p.name}`}>
            {p.goals > 1 ? `${p.goals}G` : "GOAL"}
          </span>
        )}
        {p.reduction != null && p.reduction !== p.jersey && (
          <span className="font-mono text-[10px] text-slate-500">→ {p.reduction}</span>
        )}
        <span className={`rounded px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider ${MATCH_STYLE[p.match] || "bg-slate-800 text-slate-500"}`}>
          {p.match.replace(" MATCH", "").replace("REDUCTION GROUP", "GROUP")}
        </span>
      </div>
    </div>
  );
};

const NUM_COLOR = {
  "DIRECT MATCH": "text-lime-300",
  "REDUCED MATCH": "text-cyan-300",
  "REDUCTION GROUP MATCH": "text-amber-300",
  "NO MATCH": "text-slate-500",
};

const ScorerLog = ({ apiDate }) => {
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/numerology-log`, { timeout: 60000 })
      .then((r) => { if (alive) setLog(r.data); })
      .catch(() => { if (alive) setLog({ days: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apiDate]);

  if (loading) return (
    <div className="flex items-center justify-center py-10 text-slate-500" data-testid="numlog-loading">
      <Loader2 className="h-5 w-5 animate-spin text-fuchsia-400" />
    </div>
  );
  const days = log?.days || [];
  if (!days.length) return (
    <p className="py-10 text-center font-mono text-[11px] uppercase tracking-widest text-slate-600" data-testid="numlog-empty">
      No scorers logged yet — open Numerology on a slate with completed goals to start the log
    </p>
  );

  return (
    <div className="space-y-3" data-testid="numlog-list">
      {days.map((day) => (
        <div key={day.date} className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/30" data-testid={`numlog-day-${day.date}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 bg-slate-900/60 px-3 py-2">
            <span className="font-display text-[13px] font-bold uppercase tracking-wide text-fuchsia-300">{day.date_label || day.date}</span>
            <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">
              {day.scorers.length} scorers · {day.total_goals} goals · <span className="text-lime-400">{day.aligned_count} aligned</span>
            </span>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] border-collapse">
            <thead>
              <tr className="bg-slate-900/40 font-mono text-[9px] uppercase tracking-wider text-slate-500">
                <th className="px-2 py-1.5 text-left">Team</th>
                <th className="px-2 py-1.5 text-left">#</th>
                <th className="px-2 py-1.5 text-left">Player</th>
                <th className="px-2 py-1.5 text-left">Pos</th>
                <th className="px-2 py-1.5 text-right">Goals</th>
                <th className="px-2 py-1.5 text-right">Numerology #</th>
                <th className="px-2 py-1.5 text-right">Match</th>
              </tr>
            </thead>
            <tbody>
              {day.scorers.map((r, i) => (
                <tr key={`${r.team}-${r.jersey}-${i}`} className={`border-t border-slate-800/60 ${r.aligned ? "bg-lime-500/5" : ""}`} data-testid={`numlog-row-${r.team}-${r.jersey}`}>
                  <td className="px-2 py-1.5 font-mono text-[11px] font-bold text-slate-300">{r.team}</td>
                  <td className="px-2 py-1.5 font-mono text-[11px] text-cyan-400">#{r.jersey ?? "—"}</td>
                  <td className="whitespace-nowrap px-2 py-1.5 font-display text-[12px] font-semibold uppercase text-slate-100">
                    {r.aligned && <Flame className="mr-1 inline h-3 w-3 text-lime-400" />}<PlayerName name={r.name} team={r.team} />
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[10px] text-slate-400">{r.position || "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-[12px] font-bold text-rose-300">{r.goals}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-[12px] font-bold text-fuchsia-300">{r.numerology_number ?? "—"}</td>
                  <td className={`whitespace-nowrap px-2 py-1.5 text-right font-mono text-[9px] font-bold uppercase ${NUM_COLOR[r.match] || "text-slate-500"}`}>
                    {(r.match || "NO MATCH").replace(" MATCH", "").replace("REDUCTION GROUP", "GROUP")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function NumerologyTab({ apiDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [gameF, setGameF] = useState("ALL");
  const [teamF, setTeamF] = useState("ALL");
  const [matchOnly, setMatchOnly] = useState(false);
  const [hitOnly, setHitOnly] = useState(false);
  const [view, setView] = useState("alignment");

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const params = apiDate ? { date: apiDate } : {};
      const res = await axios.get(`${API}/numerology`, { params, timeout: 240000 });
      setData(res.data);
    } catch (e) { console.error(e); setError(true); }
    finally { setLoading(false); }
  }, [apiDate]);
  useEffect(() => { load(); }, [load]);

  const games = useMemo(() => {
    if (!data?.games) return [];
    let gs = data.games;
    if (gameF !== "ALL") gs = gs.filter((g) => g.game === gameF);
    return gs;
  }, [data, gameF]);

  const teams = useMemo(() => data?.games?.flatMap((g) => g.teams.map((t) => t.team)) || [], [data]);

  const filterPlayers = (players) => {
    let ps = players;
    if (hitOnly) ps = ps.filter((p) => p.aligned_scored);
    else if (matchOnly) ps = ps.filter((p) => p.match !== "NO MATCH");
    return ps;
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-32 text-slate-500" data-testid="numerology-loading">
      <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
      <p className="mt-4 font-mono text-xs uppercase tracking-widest">Aligning jersey numbers…</p>
    </div>
  );
  if (error || !data) return (
    <div className="flex flex-col items-center justify-center py-32 text-slate-500" data-testid="numerology-error">
      <p className="font-mono text-xs uppercase tracking-widest text-rose-400">Numerology unavailable</p>
      <button onClick={load} className="mt-3 rounded bg-slate-800 px-3 py-1 font-mono text-[11px] text-slate-300 hover:bg-slate-700">Retry</button>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="numerology-dashboard">
      <DailyHeader daily={data.daily} />
      <div className="flex gap-1.5" data-testid="numerology-views">
        {[["alignment", "Jersey Alignment"], ["aligned", "Aligned-Day Scorers"], ["log", "Scorer Log"]].map(([k, l]) => (
          <button
            key={k}
            data-testid={`num-view-${k}`}
            onClick={() => setView(k)}
            className={`rounded-lg px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider transition ${
              view === k ? "bg-fuchsia-500/20 text-fuchsia-300 shadow-[inset_0_0_0_1px_rgba(217,70,239,0.5)]" : "border border-slate-800 text-slate-500 hover:text-slate-300"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {view === "log" ? (
        <ScorerLog apiDate={apiDate} />
      ) : view === "aligned" ? (
        <NumerologyAlignedScorers apiDate={apiDate} />
      ) : (
      <>
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-fuchsia-400">Jersey Number Alignment</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900/60 px-2 py-1">
          <select data-testid="num-game-filter" value={gameF} onChange={(e) => setGameF(e.target.value)} className="bg-transparent font-mono text-[10px] uppercase text-slate-300 focus:outline-none">
            <option value="ALL">All Games</option>
            {data.games.map((g) => <option key={g.game} value={g.game}>{g.game}</option>)}
          </select>
        </div>
        <button
          data-testid="num-match-toggle"
          onClick={() => setMatchOnly((m) => !m)}
          className={`rounded-lg px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider transition ${matchOnly ? "bg-lime-500/20 text-lime-300 shadow-[inset_0_0_0_1px_rgba(163,230,53,0.45)]" : "border border-slate-800 text-slate-500 hover:text-slate-300"}`}
        >
          Matches Only
        </button>
        <button
          data-testid="num-hit-toggle"
          onClick={() => setHitOnly((m) => !m)}
          className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider transition ${hitOnly ? "bg-lime-500/25 text-lime-200 shadow-[inset_0_0_0_1px_rgba(163,230,53,0.6)]" : "border border-slate-800 text-slate-500 hover:text-slate-300"}`}
        >
          <Flame className="h-3.5 w-3.5" /> Aligned + Scored
          {data.aligned_scorers > 0 && (
            <span className="rounded-full bg-lime-500 px-1.5 text-[9px] font-bold text-slate-950">{data.aligned_scorers}</span>
          )}
        </button>
      </div>
      <div className="space-y-3" data-testid="numerology-games">
        {games.map((g) => (
          <div key={g.game} className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/30" data-testid={`num-game-${g.away}-${g.home}`}>
            <div className="border-b border-slate-800 bg-slate-900/60 px-3 py-2 font-display text-[13px] font-bold uppercase tracking-wide text-slate-100">{g.game}</div>
            <div className="grid grid-cols-1 gap-2 p-2.5 sm:grid-cols-2">
              {g.teams.map((tm) => {
                const ps = filterPlayers(tm.players);
                return (
                  <div key={tm.team}>
                    <p className="mb-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-cyan-300">{tm.team_name}</p>
                    <div className="space-y-1">
                      {ps.length ? ps.map((p, i) => <PlayerRow key={`${p.name}-${i}`} p={p} team={tm.team} />) : <p className="py-2 text-center font-mono text-[10px] text-slate-600">No alignments</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      </>
      )}
    </div>
  );
}
